import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  ActionInstructions,
  DeliveryTiming,
  PeriodLimit,
  RecurringDateSpec,
} from "@/types";
import { parseIsoDate } from "@/dashboard/production_strategy/overview/PPA/ppaPortfolioUtils";
import { parseIsoDuration } from "@/utils/durationUtils";
import {
  add,
  addDays,
  addMonths,
  addYears,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  getDaysInMonth,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "date-fns";

export type ExecutionPeriod = { start: string; end: string };

export interface ExecutionSummaryEntry {
  executionDate: Date;
  formattedDate: string;
  cancellationPeriod: ExecutionPeriod | null;
  executionPeriod: ExecutionPeriod | null;
}

type ProjectionByMonthResponse = Record<string, number>;

function normalizeDate(date: Date): Date {
  return startOfDay(date);
}

function resolveExecutionDate(monthStart: Date, requestedDay: number): Date {
  const daysInThisMonth = getDaysInMonth(monthStart);
  const effectiveDay =
    requestedDay === 31
      ? daysInThisMonth
      : Math.min(requestedDay, daysInThisMonth);
  return addDays(monthStart, effectiveDay - 1);
}

function createDateFromSpec(year: number, spec: RecurringDateSpec): Date {
  const monthStart = startOfMonth(new Date(year, spec.month - 1, 1));
  return resolveExecutionDate(monthStart, spec.day);
}

function shiftMonth(
  year: number,
  month: number,
  delta: number
): {
  year: number;
  month: number;
} {
  const initial = startOfMonth(new Date(year, month - 1, 1));
  const shifted = addMonths(initial, delta);
  return {
    year: shifted.getFullYear(),
    month: shifted.getMonth() + 1,
  };
}

function startOfMonthDate(year: number, month: number): Date {
  return startOfMonth(new Date(year, month - 1, 1));
}

function endOfMonthDate(year: number, month: number): Date {
  return endOfMonth(new Date(year, month - 1, 1));
}

function sortSpecs(specs: RecurringDateSpec[]): RecurringDateSpec[] {
  return [...specs].sort((a, b) => {
    if (a.month === b.month) {
      return a.day - b.day;
    }
    return a.month - b.month;
  });
}

function getNextYearlyDate(
  reference: Date,
  spec: RecurringDateSpec | undefined,
  endDate: Date | null
): Date | null {
  if (!spec?.month || !spec?.day) {
    return null;
  }

  const currentYear = reference.getFullYear();
  const thisYear = createDateFromSpec(currentYear, spec);
  const nextYear = createDateFromSpec(currentYear + 1, spec);

  const nextDate = reference <= thisYear ? thisYear : nextYear;

  if (endDate && nextDate > endDate) {
    return null;
  }

  return nextDate;
}

function getNextHalfYearDate(
  reference: Date,
  specs: RecurringDateSpec[],
  endDate: Date | null
): Date | null {
  if (!specs || specs.length !== 2) {
    return null;
  }

  const sorted = sortSpecs(specs);
  const currentYear = reference.getFullYear();
  const firstHalf = createDateFromSpec(currentYear, sorted[0]);
  const secondHalf = createDateFromSpec(currentYear, sorted[1]);
  const nextYearFirst = createDateFromSpec(currentYear + 1, sorted[0]);

  let nextDate: Date;
  if (reference <= firstHalf) {
    nextDate = firstHalf;
  } else if (reference <= secondHalf) {
    nextDate = secondHalf;
  } else {
    nextDate = nextYearFirst;
  }

  if (endDate && nextDate > endDate) {
    return null;
  }

  return nextDate;
}

function getNextQuarterlyDate(
  reference: Date,
  specs: RecurringDateSpec[],
  endDate: Date | null
): Date | null {
  if (!specs || specs.length !== 4) {
    return null;
  }

  const sorted = sortSpecs(specs).map((spec) =>
    createDateFromSpec(reference.getFullYear(), spec)
  );

  for (const quarterDate of sorted) {
    if (reference <= quarterDate) {
      if (endDate && quarterDate > endDate) {
        return null;
      }
      return quarterDate;
    }
  }

  const nextYearDates = sortSpecs(specs).map((spec) =>
    createDateFromSpec(reference.getFullYear() + 1, spec)
  );
  const nextDate = nextYearDates[0];

  if (endDate && nextDate > endDate) {
    return null;
  }

  return nextDate;
}

function getNextMonthlyDate(
  reference: Date,
  monthlyDay: number | undefined,
  endDate: Date | null
): Date | null {
  if (!monthlyDay) {
    return null;
  }

  const monthStart = startOfMonth(
    new Date(reference.getFullYear(), reference.getMonth(), 1)
  );

  const thisMonth = resolveExecutionDate(monthStart, monthlyDay);
  if (reference <= thisMonth) {
    if (endDate && thisMonth > endDate) {
      return null;
    }
    return thisMonth;
  }

  const nextMonthStart = startOfMonth(addMonths(monthStart, 1));
  const nextMonth = resolveExecutionDate(nextMonthStart, monthlyDay);

  if (endDate && nextMonth > endDate) {
    return null;
  }

  return nextMonth;
}

function getNextFixedDate(
  reference: Date,
  specs: RecurringDateSpec[],
  endDate: Date | null
): Date | null {
  if (!specs || specs.length === 0) {
    return null;
  }

  const currentYear = reference.getFullYear();
  const sortedSpecs = sortSpecs(specs);
  const upcoming: Date[] = [];

  sortedSpecs.forEach((spec) => {
    const candidate = createDateFromSpec(currentYear, spec);
    if (reference <= candidate) {
      upcoming.push(candidate);
    }
  });

  if (upcoming.length === 0) {
    sortedSpecs.forEach((spec) => {
      upcoming.push(createDateFromSpec(currentYear + 1, spec));
    });
  }

  if (upcoming.length === 0) {
    return null;
  }

  upcoming.sort((a, b) => a.getTime() - b.getTime());
  const nextDate = upcoming[0];

  if (endDate && nextDate > endDate) {
    return null;
  }

  return nextDate;
}

export function getNextExecutionDate(
  instructions: ActionInstructions,
  referenceDate: Date = new Date()
): Date | null {
  // Both bounds are date-only values, so they must be read as local calendar days — `new Date("2026-04-01")`
  // parses as UTC midnight and lands on the previous day in a negative-offset browser.
  const endDate = instructions.end_date
    ? endOfDay(parseIsoDate(instructions.end_date))
    : null;
  // The scanner skips window dates before start_date, so a contract that has not begun must not preview an
  // execution it will never run. Clamping the reference up to the start makes every timing helper below
  // return the first date on or after it, matching the backend's inclusive lower bound.
  const startDate = instructions.start_date
    ? parseIsoDate(instructions.start_date)
    : null;
  const reference = normalizeDate(referenceDate);
  const normalizedReference =
    startDate && reference < startDate ? startDate : reference;

  if (endDate && normalizedReference > endDate) {
    return null;
  }

  const { delivery_timing, recurring_date_specs } = instructions;

  switch (delivery_timing) {
    case "YEARLY":
      return getNextYearlyDate(
        normalizedReference,
        recurring_date_specs[0],
        endDate
      );
    case "HALF_YEAR":
      return getNextHalfYearDate(
        normalizedReference,
        recurring_date_specs,
        endDate
      );
    case "QUARTERLY":
      return getNextQuarterlyDate(
        normalizedReference,
        recurring_date_specs,
        endDate
      );
    case "MONTHLY":
      return getNextMonthlyDate(
        normalizedReference,
        recurring_date_specs[0]?.day,
        endDate
      );
    case "FIXED_DATES":
      return getNextFixedDate(
        normalizedReference,
        recurring_date_specs,
        endDate
      );
    default:
      return null;
  }
}

export function calculateNextExecutionDate(
  instructions: ActionInstructions
): string | null {
  const nextDate = getNextExecutionDate(instructions);
  if (!nextDate) {
    return null;
  }
  return formatDateTimeIntoDate(nextDate);
}

function getPreviousExecutionDate(
  instructions: ActionInstructions,
  executionDate: Date
): Date | null {
  const specs = instructions.recurring_date_specs;
  if (!specs.length) {
    return null;
  }

  const executionDay = normalizeDate(executionDate);
  const candidates: Date[] = [];
  const offsets = [-2, -1, 0];

  offsets.forEach((offset) => {
    const year = executionDay.getFullYear() + offset;
    specs.forEach((spec) => {
      candidates.push(createDateFromSpec(year, spec));
    });
  });

  const normalizedCandidates = candidates
    .map((candidate) => normalizeDate(candidate))
    .sort((a, b) => a.getTime() - b.getTime());

  const index = normalizedCandidates.findIndex(
    (candidate) => candidate.getTime() === executionDay.getTime()
  );

  if (index <= 0) {
    return null;
  }

  return normalizedCandidates[index - 1];
}

function formatDateToIso(dateValue: Date): string {
  return format(dateValue, "yyyy-MM-dd");
}

function calculateMonthlyCancellation(executionDate: Date): {
  start: string;
  end: string;
} {
  const { year, month } = shiftMonth(
    executionDate.getFullYear(),
    executionDate.getMonth() + 1,
    -1
  );
  const start = startOfMonthDate(year, month);
  const end = endOfMonthDate(year, month);
  return { start: formatDateToIso(start), end: formatDateToIso(end) };
}

function calculateQuarterlyCancellation(executionDate: Date): {
  start: string;
  end: string;
} {
  const currentMonth = executionDate.getMonth() + 1;
  const { year: startYear, month: startMonth } = shiftMonth(
    executionDate.getFullYear(),
    currentMonth,
    -3
  );
  const { year: endYear, month: endMonth } = shiftMonth(
    executionDate.getFullYear(),
    currentMonth,
    -1
  );
  const start = startOfMonthDate(startYear, startMonth);
  const end = endOfMonthDate(endYear, endMonth);
  return { start: formatDateToIso(start), end: formatDateToIso(end) };
}

function calculateHalfYearCancellation(executionDate: Date): {
  start: string;
  end: string;
} {
  const currentMonth = executionDate.getMonth() + 1;
  const inFirstHalf = currentMonth <= 6;

  if (inFirstHalf) {
    const start = startOfMonthDate(executionDate.getFullYear() - 1, 7);
    const end = endOfMonthDate(executionDate.getFullYear() - 1, 12);
    return { start: formatDateToIso(start), end: formatDateToIso(end) };
  }

  const start = startOfMonthDate(executionDate.getFullYear(), 1);
  const end = endOfMonthDate(executionDate.getFullYear(), 6);
  return { start: formatDateToIso(start), end: formatDateToIso(end) };
}

function calculateYearlyCancellation(executionDate: Date): {
  start: string;
  end: string;
} {
  const previousYear = addYears(executionDate, -1);
  const start = startOfYear(previousYear);
  const end = endOfYear(previousYear);
  return { start: formatDateToIso(start), end: formatDateToIso(end) };
}

function calculateFixedDatesCancellation(
  instructions: ActionInstructions,
  executionDate: Date
): ExecutionPeriod | null {
  const previousDate = getPreviousExecutionDate(instructions, executionDate);
  if (!previousDate) {
    return null;
  }

  const start = startOfMonthDate(
    previousDate.getFullYear(),
    previousDate.getMonth() + 1
  );

  const { year: endYear, month: endMonth } = shiftMonth(
    executionDate.getFullYear(),
    executionDate.getMonth() + 1,
    -1
  );
  const end = endOfMonthDate(endYear, endMonth);

  return { start: formatDateToIso(start), end: formatDateToIso(end) };
}

function calculateExecutionPeriodForTiming(
  instructions: ActionInstructions,
  executionDate: Date
): ExecutionPeriod | null {
  const timing: DeliveryTiming = instructions.delivery_timing;

  switch (timing) {
    case "MONTHLY":
      return calculateMonthlyCancellation(executionDate);
    case "QUARTERLY":
      return calculateQuarterlyCancellation(executionDate);
    case "HALF_YEAR":
      return calculateHalfYearCancellation(executionDate);
    case "YEARLY":
      return calculateYearlyCancellation(executionDate);
    case "FIXED_DATES":
      return calculateFixedDatesCancellation(instructions, executionDate);
    default:
      return null;
  }
}

export function getUpcomingExecutions(
  instructions: ActionInstructions,
  count = 2,
  referenceDate: Date = new Date()
): ExecutionSummaryEntry[] {
  const results: ExecutionSummaryEntry[] = [];
  let cursor = normalizeDate(referenceDate);
  // Same local-calendar-day parse as getNextExecutionDate; a UTC-midnight ceiling here would drop a final
  // delivery the inner helper had already accepted.
  const endDate = instructions.end_date
    ? endOfDay(parseIsoDate(instructions.end_date))
    : null;

  while (results.length < count) {
    const nextDate = getNextExecutionDate(instructions, cursor);
    if (!nextDate) {
      break;
    }

    if (endDate && nextDate > endDate) {
      break;
    }

    const executionPeriod = calculateExecutionPeriodForTiming(
      instructions,
      nextDate
    );
    const cancellationPeriod =
      instructions.transaction_type === "CANCEL" ? executionPeriod : null;

    results.push({
      executionDate: nextDate,
      formattedDate: formatDateTimeIntoDate(nextDate),
      cancellationPeriod,
      executionPeriod,
    });

    cursor = normalizeDate(addDays(nextDate, 1));
  }

  return results;
}

function enumerateMonthsBetween(startIso: string, endIso: string): number[] {
  const [startYear, startMonth] = startIso.split("-").map(Number);
  const [endYear, endMonth] = endIso.split("-").map(Number);

  const months: number[] = [];
  let currentYear = startYear;
  let currentMonth = startMonth;

  while (
    currentYear < endYear ||
    (currentYear === endYear && currentMonth <= endMonth)
  ) {
    months.push(currentMonth);
    currentMonth += 1;
    if (currentMonth > 12) {
      currentMonth = 1;
      currentYear += 1;
    }
  }

  return months;
}

/**
 * Fetches the production projection for a company's devices.
 * Returns the full 12-month projection data.
 *
 * @throws When the API request fails
 */
export async function fetchProjectionData(
  companyId: number,
  deviceIds: string[]
): Promise<ProjectionByMonthResponse | null> {
  if (!companyId || deviceIds.length === 0) {
    return null;
  }

  const response = await goSolidApi.get<{
    projection: ProjectionByMonthResponse;
  }>(`/api/company/${companyId}/registry-devices/projection`, {
    params: {
      device_ids: deviceIds.join(","),
    },
  });

  return response.data?.projection ?? null;
}

/**
 * Calculates expected volume from a pre-fetched projection for a specific period.
 */
export function calculateVolumeFromProjection(
  projection: ProjectionByMonthResponse,
  period: { start: string; end: string },
  allocatePercentage?: number
): number {
  const months = enumerateMonthsBetween(period.start, period.end);
  const totalVolume = months.reduce((sum, month) => {
    const value = projection[String(month)] ?? 0;
    return sum + value;
  }, 0);

  if (allocatePercentage != null && allocatePercentage !== 100) {
    return totalVolume * (allocatePercentage / 100);
  }

  return totalVolume;
}

/**
 * Finds the matching period limit for a given execution period.
 * Returns the limit whose period contains the execution period's start date.
 */
export function findMatchingLimitForPeriod(
  limits: PeriodLimit[],
  period: ExecutionPeriod
): PeriodLimit | null {
  if (!limits || limits.length === 0 || !period) {
    return null;
  }

  const periodStart = new Date(period.start);

  for (const limit of limits) {
    const limitStart = new Date(limit.period.start_date);
    const limitEnd = add(limitStart, parseIsoDuration(limit.period.duration));

    if (periodStart >= limitStart && periodStart < limitEnd) {
      return limit;
    }
  }

  return null;
}
